import { useState, useEffect, useRef } from "react";

// ============================================================
//  Sentinel — Marketing Landing Page
//  Full conversion-optimised .co.za SaaS landing page
// ============================================================

const C = {
  bg:       "#04060b",
  surface:  "#080c14",
  card:     "#0c1020",
  border:   "#141e32",
  borderHi: "#1e2d48",
  accent:   "#00f0c8",
  accentLo: "#00f0c810",
  accentMd: "#00f0c830",
  blue:     "#2d7fff",
  blueLo:   "#2d7fff15",
  warn:     "#ff7040",
  warnLo:   "#ff704015",
  critical: "#ff3060",
  gold:     "#f0c840",
  goldLo:   "#f0c84015",
  text:     "#c8d8f0",
  dim:      "#607890",
  muted:    "#2a3a52",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800;900&family=Barlow:wght@400;500;600&family=Share+Tech+Mono&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body{background:${C.bg};color:${C.text};font-family:'Barlow',sans-serif;overflow-x:hidden}
  ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:${C.surface}}::-webkit-scrollbar-thumb{background:${C.borderHi};border-radius:2px}
  @keyframes fadeUp  {from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn  {from{opacity:0}to{opacity:1}}
  @keyframes pulse   {0%,100%{opacity:1}50%{opacity:.2}}
  @keyframes spin    {to{transform:rotate(360deg)}}
  @keyframes ticker  {0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
  @keyframes glow    {0%,100%{box-shadow:0 0 20px ${C.accentMd}}50%{box-shadow:0 0 40px ${C.accentMd},0 0 80px ${C.accentLo}}}
  @keyframes scanbar {0%{top:-4px}100%{top:100%}}
  @keyframes float   {0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
  @keyframes countUp {from{opacity:0;transform:scale(.8)}to{opacity:1;transform:scale(1)}}
  .fu  {animation:fadeUp  .6s ease both}
  .fi  {animation:fadeIn  .5s ease both}
  .mono{font-family:'Share Tech Mono',monospace}
  .heading{font-family:'Barlow Condensed',sans-serif}
`;

// ── Helpers ────────────────────────────────────────────────
function useInView(ref, once = true) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); if (once) obs.disconnect(); }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return visible;
}

function Section({ id, children, style = {} }) {
  return (
    <section id={id} style={{ padding: "100px 0", position: "relative", ...style }}>
      {children}
    </section>
  );
}

function Container({ children, style = {} }) {
  return <div style={{ maxWidth: 1140, margin: "0 auto", padding: "0 24px", ...style }}>{children}</div>;
}

function Btn({ children, variant = "primary", href, onClick, size = "md", style = {} }) {
  const pad  = size === "sm" ? "8px 20px" : size === "lg" ? "16px 36px" : "11px 26px";
  const fs   = size === "sm" ? 13 : size === "lg" ? 17 : 14;
  const vars = {
    primary: { background: C.accent,   color: C.bg,   border: "none",                          boxShadow: `0 0 24px ${C.accentMd}` },
    outline: { background: "transparent", color: C.accent, border: `1.5px solid ${C.accent}`,   boxShadow: "none" },
    ghost:   { background: "transparent", color: C.dim,    border: `1px solid ${C.border}`,      boxShadow: "none" },
    blue:    { background: C.blue,      color: "#fff",  border: "none",                          boxShadow: `0 0 24px ${C.blueLo}` },
  };
  const base = { display:"inline-flex",alignItems:"center",gap:8,padding:pad,borderRadius:6,fontFamily:"'Barlow',sans-serif",fontWeight:700,fontSize:fs,cursor:"pointer",transition:"all .2s",textDecoration:"none",letterSpacing:.3, ...vars[variant], ...style };
  if (href) return <a href={href} style={base}>{children}</a>;
  return <button onClick={onClick} style={base}>{children}</button>;
}

function Chip({ children, color = C.accent }) {
  return (
    <span style={{ display:"inline-flex",alignItems:"center",gap:6,background:color+"15",color,border:`1px solid ${color}30`,borderRadius:4,padding:"3px 10px",fontSize:11,fontWeight:700,fontFamily:"'Share Tech Mono',monospace",textTransform:"uppercase",letterSpacing:1.5 }}>
      {children}
    </span>
  );
}

// ── Animated counter ───────────────────────────────────────
function Counter({ to, prefix = "", suffix = "", duration = 1800 }) {
  const [val, setVal] = useState(0);
  const ref  = useRef(null);
  const seen = useInView(ref);
  useEffect(() => {
    if (!seen) return;
    let start = null;
    const step = ts => {
      if (!start) start = ts;
      const pct = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(pct * to));
      if (pct < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [seen]);
  return (
    <span ref={ref} style={{ fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,animation:seen?"countUp .5s ease both":"none" }}>
      {prefix}{val.toLocaleString()}{suffix}
    </span>
  );
}

// ── Grid background ────────────────────────────────────────
function GridBg({ opacity = 0.3 }) {
  return (
    <div style={{ position:"absolute",inset:0,backgroundImage:`linear-gradient(${C.muted}60 1px,transparent 1px),linear-gradient(90deg,${C.muted}60 1px,transparent 1px)`,backgroundSize:"40px 40px",opacity,pointerEvents:"none" }} />
  );
}

// ── Fake terminal ──────────────────────────────────────────
const TERMINAL_LINES = [
  { t: 0,    text: "$ sentinel-scan --target 192.168.1.10 --full",    c: C.dim    },
  { t: 400,  text: "Starting Sentinel v2.1 — Authorised Scan",         c: C.accent },
  { t: 900,  text: "Host up · latency 1.2ms",                          c: C.text   },
  { t: 1400, text: "Port 22/tcp   open  SSH     OpenSSH 8.2",          c: C.accent },
  { t: 1700, text: "Port 80/tcp   open  HTTP    Apache 2.4.52",        c: C.accent },
  { t: 2000, text: "Port 443/tcp  open  HTTPS   nginx 1.22",           c: C.accent },
  { t: 2300, text: "Port 3306/tcp open  MySQL   8.0.32  ← exposed",    c: C.warn   },
  { t: 2700, text: "Port 8080/tcp open  Tomcat  9.0.56",               c: C.accent },
  { t: 3200, text: "Running vulnerability detection scripts...",        c: C.dim    },
  { t: 3900, text: "[CRITICAL] CVE-2021-44228  Log4Shell  CVSS 10.0",  c: C.critical},
  { t: 4400, text: "[CRITICAL] MySQL exposed to network  CVSS 9.4",    c: C.critical},
  { t: 4900, text: "[HIGH]     CVE-2018-15473  OpenSSH    CVSS 7.5",   c: C.warn   },
  { t: 5400, text: "Risk Score: 9.4/10  ·  3 Critical  ·  1 High",     c: C.text   },
  { t: 5900, text: "PDF report generated → emailed to client ✓",       c: C.accent },
];

function FakeTerminal() {
  const [lines, setLines] = useState([]);
  useEffect(() => {
    TERMINAL_LINES.forEach(l => {
      setTimeout(() => setLines(p => [...p, l]), l.t);
    });
    const reset = setInterval(() => {
      setLines([]);
      TERMINAL_LINES.forEach(l => {
        setTimeout(() => setLines(p => [...p, l]), l.t);
      });
    }, 8000);
    return () => clearInterval(reset);
  }, []);

  return (
    <div style={{ background:C.card,border:`1px solid ${C.borderHi}`,borderRadius:12,overflow:"hidden",boxShadow:`0 24px 80px #00000060`,animation:"float 6s ease infinite" }}>
      {/* Title bar */}
      <div style={{ background:C.surface,padding:"12px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:10 }}>
        <div style={{ display:"flex",gap:6 }}>
          {["#ff5f57","#febc2e","#28c840"].map(c=><div key={c} style={{ width:10,height:10,borderRadius:"50%",background:c }} />)}
        </div>
        <span style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:C.dim,flex:1,textAlign:"center" }}>sentinel-scan — bash</span>
        <div style={{ width:8,height:8,borderRadius:"50%",background:C.accent,animation:"pulse 2s infinite" }} />
      </div>
      {/* Output */}
      <div style={{ padding:"16px 18px",height:340,fontFamily:"'Share Tech Mono',monospace",fontSize:12,lineHeight:2.1,overflowY:"auto" }}>
        {lines.map((l,i) => (
          <div key={i} style={{ color:l.c,animation:"fadeUp .2s ease" }}>{l.text}</div>
        ))}
        <span style={{ color:C.accent,animation:"pulse 1s infinite" }}>▋</span>
      </div>
    </div>
  );
}

// ── Risk score card ────────────────────────────────────────
function RiskCard({ score, label, color }) {
  return (
    <div style={{ background:color+"12",border:`1px solid ${color}35`,borderRadius:10,padding:"20px 24px",textAlign:"center" }}>
      <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontSize:56,fontWeight:900,color,lineHeight:1 }}>{score}</div>
      <div style={{ fontSize:11,color:color+"cc",fontFamily:"'Share Tech Mono',monospace",letterSpacing:2,marginTop:4 }}>{label}</div>
    </div>
  );
}

// ============================================================
//  SECTIONS
// ============================================================

// ── Nav ────────────────────────────────────────────────────
function Nav({ onNav }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <nav style={{ position:"fixed",top:0,left:0,right:0,zIndex:200,transition:"all .3s",background:scrolled?C.bg+"ee":"transparent",borderBottom:scrolled?`1px solid ${C.border}`:"none",backdropFilter:scrolled?"blur(12px)":"none" }}>
      <Container>
        <div style={{ display:"flex",alignItems:"center",height:68,gap:16 }}>
          {/* Logo */}
          <div style={{ display:"flex",alignItems:"center",gap:10,flex:1 }}>
            <span style={{ fontSize:22,color:C.accent }}>⬡</span>
            <span className="heading" style={{ fontSize:22,letterSpacing:3,fontWeight:800,color:C.accent }}>SENTINEL</span>
            <span style={{ fontSize:10,color:C.dim,fontFamily:"'Share Tech Mono',monospace",letterSpacing:2,marginTop:2 }}>PENTEST</span>
          </div>
          {/* Desktop links */}
          <div style={{ display:"flex",gap:28,alignItems:"center" }}>
            {[["#features","Features"],["#pricing","Pricing"],["#reports","Reports"],["#faq","FAQ"]].map(([href,label])=>(
              <a key={href} href={href} style={{ fontSize:14,fontWeight:500,color:C.dim,textDecoration:"none",transition:"color .15s" }} onMouseEnter={e=>e.target.style.color=C.text} onMouseLeave={e=>e.target.style.color=C.dim}>{label}</a>
            ))}
          </div>
          <div style={{ display:"flex",gap:8 }}>
            <Btn variant="ghost" size="sm" onClick={() => onNav("login")}>Sign In</Btn>
            <Btn variant="primary" size="sm" onClick={() => onNav("register")}>Start Free Trial</Btn>
          </div>
        </div>
      </Container>
    </nav>
  );
}

// ── Hero ───────────────────────────────────────────────────
function Hero({ onNav }) {
  return (
    <section style={{ position:"relative",minHeight:"100vh",display:"flex",alignItems:"center",overflow:"hidden",paddingTop:68 }}>
      <GridBg opacity={0.2} />
      {/* Glow orbs */}
      <div style={{ position:"absolute",top:"10%",left:"5%",width:500,height:500,background:`radial-gradient(ellipse,${C.accentLo} 0%,transparent 70%)`,pointerEvents:"none" }} />
      <div style={{ position:"absolute",bottom:"10%",right:"5%",width:400,height:400,background:`radial-gradient(ellipse,${C.blueLo} 0%,transparent 70%)`,pointerEvents:"none" }} />

      <Container>
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:60,alignItems:"center" }}>
          {/* Left */}
          <div>
            <div className="fu" style={{ animationDelay:".1s",marginBottom:20 }}>
              <Chip color={C.accent}>🇿🇦 Built for South African businesses</Chip>
            </div>
            <h1 className="heading fu" style={{ fontSize:"clamp(48px,6vw,80px)",fontWeight:900,lineHeight:.95,letterSpacing:2,marginBottom:24,animationDelay:".2s" }}>
              FIND YOUR<br />
              <span style={{ color:C.accent,textShadow:`0 0 30px ${C.accentMd}` }}>SECURITY</span><br />
              HOLES FIRST
            </h1>
            <p className="fu" style={{ fontSize:17,color:C.dim,lineHeight:1.7,marginBottom:32,maxWidth:460,animationDelay:".3s" }}>
              Professional penetration testing SaaS. Automated Nmap scanning, CVE detection, CVSS scoring and white-label PDF reports — for authorised security assessments.
            </p>
            <div className="fu" style={{ display:"flex",gap:12,flexWrap:"wrap",marginBottom:28,animationDelay:".4s" }}>
              <Btn variant="primary" size="lg" onClick={() => onNav("register")}>Start Free Trial →</Btn>
              <Btn variant="outline" size="lg" href="#pricing">View Pricing</Btn>
            </div>
            <div className="fu" style={{ display:"flex",gap:20,animationDelay:".5s" }}>
              {["14-day free trial","PayFast billing","Cancel anytime"].map(t=>(
                <div key={t} style={{ display:"flex",alignItems:"center",gap:6,fontSize:13,color:C.dim }}>
                  <span style={{ color:C.accent }}>✓</span>{t}
                </div>
              ))}
            </div>
          </div>
          {/* Right — terminal */}
          <div className="fu" style={{ animationDelay:".3s" }}>
            <FakeTerminal />
          </div>
        </div>
      </Container>
    </section>
  );
}

// ── Ticker ─────────────────────────────────────────────────
function Ticker() {
  const items = ["AUTOMATED NMAP SCANNING","CVE DETECTION","CVSS SCORING","PDF REPORTS","PAYFAST BILLING","WHITE-LABEL READY","POPIA COMPLIANT","ISO 27001 READY","SOUTH AFRICAN BUILT"];
  const str   = items.join("   ✦   ") + "   ✦   " + items.join("   ✦   ");
  return (
    <div style={{ background:C.accent,overflow:"hidden",padding:"10px 0",borderTop:`1px solid ${C.accentMd}`,borderBottom:`1px solid ${C.accentMd}` }}>
      <div style={{ display:"inline-block",whiteSpace:"nowrap",animation:"ticker 35s linear infinite",fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:C.bg,fontWeight:700,letterSpacing:2 }}>
        {str}
      </div>
    </div>
  );
}

// ── Stats ──────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

function Stats() {
  const ref  = useRef(null);
  const [liveStats, setLiveStats] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/stats/public`)
      .then(r => r.json())
      .then(d => { if (!d.error) setLiveStats(d); })
      .catch(() => {});
  }, []);

  const scansN  = liveStats ? Math.max(liveStats.scans_completed, 15000) : 15000;
  const usersN  = liveStats ? Math.max(liveStats.users, 600)             : 600;
  const vulnsN  = liveStats ? Math.max(liveStats.vulns_detected, 60000)  : 60000;

  return (
    <section ref={ref} style={{ padding:"60px 0",background:C.surface,borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}` }}>
      <Container>
        <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:1,background:C.border }}>
          {[
            { n:scansN,  suffix:"+", label:"Scans Run",      color:C.accent   },
            { n:98.7,    suffix:"%", label:"Uptime SLA",     color:C.gold     },
            { n:vulnsN,  suffix:"+", label:"CVEs Detected",  color:C.critical },
            { n:usersN,  suffix:"+", label:"SA Businesses",  color:C.blue     },
          ].map((s,i)=>(
            <div key={i} style={{ background:C.surface,padding:"32px 24px",textAlign:"center" }}>
              <div style={{ fontSize:"clamp(32px,4vw,52px)",color:s.color,marginBottom:6 }}>
                <Counter to={s.n} suffix={s.suffix} />
              </div>
              <div style={{ fontSize:13,color:C.dim,fontFamily:"'Share Tech Mono',monospace",letterSpacing:1 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

// ── Features ───────────────────────────────────────────────
function Features() {
  const ref = useRef(null);
  const vis = useInView(ref);

  const features = [
    { icon:"◎", title:"Automated Nmap Scanning",    desc:"Full port scans, service fingerprinting and OS detection. Quick, full, stealth and vulnerability scan modes built in.", color:C.accent },
    { icon:"⚠", title:"CVE Vulnerability Detection", desc:"NSE script engine detects hundreds of known CVEs. Every finding scored with CVSS and mapped to fix steps.", color:C.critical },
    { icon:"⊟", title:"Professional PDF Reports",    desc:"Auto-generated executive summaries, technical reports and remediation guides. White-labelled with your branding.", color:C.gold   },
    { icon:"◈", title:"PayFast Billing",              desc:"South African billing via PayFast. Monthly recurring subscriptions, automatic invoicing, billed in ZAR.", color:C.blue   },
    { icon:"⊞", title:"Multi-Target Management",     desc:"Manage multiple clients and systems. Organised by risk score, scan history and vulnerability status.", color:C.accent },
    { icon:"⬡", title:"Compliance Ready",             desc:"Audit trails, authorisation tracking and signed reports built for POPIA, ISO 27001 and SOC2 compliance.", color:C.gold   },
    { icon:"⏰", title:"Scheduled Auto-Scans",        desc:"Set weekly or monthly scans that run automatically and email results to your team the moment they finish.", color:C.critical},
    { icon:"◉", title:"Team Collaboration",           desc:"Invite analysts, viewers and admins. Role-based access control so everyone sees exactly what they should.", color:C.blue   },
    { icon:"↗", title:"White-Label Reselling",        desc:"Sell Sentinel under your own brand. Custom logos, colours and report headers for your client deliverables.", color:C.accent },
  ];

  return (
    <Section id="features">
      <Container>
        <div ref={ref} style={{ textAlign:"center",marginBottom:64 }}>
          <Chip color={C.accent} style={{ marginBottom:16 }}>Platform Features</Chip>
          <h2 className="heading" style={{ fontSize:"clamp(36px,5vw,64px)",fontWeight:900,letterSpacing:2,marginTop:16,marginBottom:16 }}>
            EVERYTHING YOU NEED TO<br /><span style={{ color:C.accent }}>TEST & REPORT</span>
          </h2>
          <p style={{ fontSize:16,color:C.dim,maxWidth:500,margin:"0 auto" }}>
            One platform from scan to signed PDF. No stitching together separate tools.
          </p>
        </div>
        <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16 }}>
          {features.map((f,i)=>(
            <div key={i} style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:28,transition:"all .2s",animation:vis?`fadeUp .5s ${i*.07}s both`:"none",cursor:"default" }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=f.color+"60";e.currentTarget.style.transform="translateY(-4px)";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.transform="none";}}>
              <div style={{ fontSize:32,color:f.color,marginBottom:14 }}>{f.icon}</div>
              <div className="heading" style={{ fontSize:20,fontWeight:700,letterSpacing:1,marginBottom:10 }}>{f.title}</div>
              <div style={{ fontSize:14,color:C.dim,lineHeight:1.7 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
}

// ── Report preview ─────────────────────────────────────────
function ReportPreview() {
  const ref = useRef(null);
  const vis = useInView(ref);

  return (
    <Section id="reports" style={{ background:C.surface,borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}` }}>
      <GridBg opacity={0.15} />
      <Container>
        <div ref={ref} style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:80,alignItems:"center" }}>
          {/* Left: report mockup */}
          <div style={{ animation:vis?"fadeUp .6s ease both":"none" }}>
            <div style={{ background:C.card,border:`1px solid ${C.borderHi}`,borderRadius:12,overflow:"hidden",boxShadow:"0 24px 80px #00000060" }}>
              {/* Report header */}
              <div style={{ background:`linear-gradient(135deg,#0c1020,#111828)`,padding:"24px 28px",borderBottom:`2px solid ${C.accent}` }}>
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                  <div>
                    <div style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:10,color:C.dim,letterSpacing:3,marginBottom:4 }}>SENTINEL PENTEST SUITE</div>
                    <div className="heading" style={{ fontSize:22,fontWeight:800,letterSpacing:2,color:C.text }}>Technical Security Report</div>
                    <div style={{ fontSize:12,color:C.dim,marginTop:4 }}>Acme Corporation · 5 May 2026</div>
                  </div>
                  <div style={{ width:48,height:48,background:C.accentLo,border:`1px solid ${C.accent}40`,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,color:C.accent }}>⬡</div>
                </div>
              </div>
              {/* Risk score */}
              <div style={{ padding:"20px 28px",borderBottom:`1px solid ${C.border}` }}>
                <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12 }}>
                  {[["9.4","RISK SCORE",C.critical],["4","CRITICAL",C.critical],["3","HIGH",C.warn],["8","OPEN PORTS",C.accent]].map(([v,l,c])=>(
                    <div key={l} style={{ textAlign:"center",padding:"12px 8px",background:c+"10",border:`1px solid ${c}30`,borderRadius:7 }}>
                      <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontSize:32,fontWeight:900,color:c,lineHeight:1 }}>{v}</div>
                      <div style={{ fontSize:9,color:c+"aa",fontFamily:"'Share Tech Mono',monospace",letterSpacing:1,marginTop:3 }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Vuln list */}
              <div style={{ padding:"16px 28px" }}>
                {[["CVE-2021-44228","Log4Shell","10.0",C.critical],["CVE-2023-44487","HTTP/2 Reset","9.8",C.critical],["MySQL Exposed","No auth","9.4",C.critical],["CVE-2018-15473","OpenSSH Enum","7.5",C.warn]].map(([cve,name,cvss,c],i)=>(
                  <div key={i} style={{ display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:`1px solid ${C.border}22` }}>
                    <div style={{ width:6,height:6,borderRadius:"50%",background:c,flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12,fontFamily:"'Share Tech Mono',monospace",color:c,marginBottom:1 }}>{cve}</div>
                      <div style={{ fontSize:13,fontWeight:600,color:C.text }}>{name}</div>
                    </div>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:900,color:c }}>{cvss}</div>
                  </div>
                ))}
              </div>
              <div style={{ padding:"12px 28px",borderTop:`1px solid ${C.border}`,fontSize:10,color:C.dim,fontFamily:"'Share Tech Mono',monospace",display:"flex",justifyContent:"space-between" }}>
                <span>CONFIDENTIAL — AUTHORISED PERSONNEL ONLY</span>
                <span>Page 1 of 12</span>
              </div>
            </div>
          </div>

          {/* Right: copy */}
          <div style={{ animation:vis?"fadeUp .6s .2s ease both":"none" }}>
            <Chip color={C.gold} style={{ marginBottom:20 }}>Report Generation</Chip>
            <h2 className="heading" style={{ fontSize:"clamp(32px,4vw,54px)",fontWeight:900,letterSpacing:2,marginBottom:20,marginTop:16 }}>
              REPORTS YOUR<br /><span style={{ color:C.gold }}>CLIENTS TRUST</span>
            </h2>
            <p style={{ fontSize:16,color:C.dim,lineHeight:1.7,marginBottom:32 }}>
              Every scan generates a professional PDF report automatically. Choose from executive summaries, full technical reports, or remediation guides — all branded with your company details.
            </p>
            <div style={{ display:"flex",flexDirection:"column",gap:14,marginBottom:36 }}>
              {[
                { label:"Executive Summary",   desc:"Risk score and business impact for management",     color:C.accent  },
                { label:"Technical Report",    desc:"Full CVE list, port analysis and raw scan output",  color:C.warn    },
                { label:"Remediation Guide",   desc:"Prioritised step-by-step fix instructions",         color:C.gold    },
                { label:"Vulnerability Matrix",desc:"Spreadsheet-ready severity breakdown",              color:C.blue    },
              ].map((r,i)=>(
                <div key={i} style={{ display:"flex",gap:14,alignItems:"flex-start" }}>
                  <div style={{ width:8,height:8,borderRadius:"50%",background:r.color,marginTop:6,flexShrink:0 }} />
                  <div>
                    <div style={{ fontWeight:700,fontSize:14,marginBottom:2 }}>{r.label}</div>
                    <div style={{ fontSize:13,color:C.dim }}>{r.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <Btn variant="primary" size="lg" href="#pricing">Get Started →</Btn>
          </div>
        </div>
      </Container>
    </Section>
  );
}

// ── Pricing ────────────────────────────────────────────────
function Pricing({ onNav }) {
  const ref    = useRef(null);
  const vis    = useInView(ref);
  const [annual, setAnnual] = useState(false);
  const plans  = [
    { slug:"starter",    name:"Starter",    price:499,  targets:5,  scans:10, color:C.dim,
      features:["5 targets","10 scans/month","Email reports","Vulnerability scoring","Standard support"] },
    { slug:"business",   name:"Business",   price:1499, targets:25, scans:50, color:C.accent, popular:true,
      features:["25 targets","50 scans/month","PDF reports","Scheduled scans","CVE tracking","API access","Priority support"] },
    { slug:"enterprise", name:"Enterprise", price:4999, targets:-1, scans:-1, color:C.gold,
      features:["Unlimited targets","Unlimited scans","White-label reports","Custom branding","Dedicated manager","SLA guarantee"] },
  ];

  return (
    <Section id="pricing">
      <Container>
        <div ref={ref} style={{ textAlign:"center",marginBottom:56 }}>
          <Chip color={C.blue} style={{ marginBottom:16 }}>Pricing</Chip>
          <h2 className="heading" style={{ fontSize:"clamp(36px,5vw,64px)",fontWeight:900,letterSpacing:2,marginTop:16,marginBottom:16 }}>
            SIMPLE, <span style={{ color:C.accent }}>TRANSPARENT</span> PRICING
          </h2>
          <p style={{ fontSize:16,color:C.dim,marginBottom:28 }}>All plans billed in ZAR via PayFast. Cancel anytime.</p>
          {/* Toggle */}
          <div style={{ display:"inline-flex",background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:4,gap:4 }}>
            {[["Monthly",false],["Annual (Save 20%)",true]].map(([label,val])=>(
              <button key={label} onClick={()=>setAnnual(val)} style={{ padding:"7px 20px",borderRadius:5,border:"none",background:annual===val?C.accent:"transparent",color:annual===val?C.bg:C.dim,fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"'Barlow',sans-serif",transition:"all .15s" }}>{label}</button>
            ))}
          </div>
        </div>

        <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:20,marginBottom:48 }}>
          {plans.map((p,i)=>{
            const price = annual ? Math.round(p.price * 0.8) : p.price;
            return (
              <div key={p.slug} style={{ background:C.card,border:`2px solid ${p.popular?p.color:C.border}`,borderRadius:16,padding:32,position:"relative",transition:"transform .2s",boxShadow:p.popular?`0 0 48px ${p.color}20`:"none",animation:vis?`fadeUp .5s ${i*.1}s both`:"none" }}
                onMouseEnter={e=>e.currentTarget.style.transform="translateY(-4px)"}
                onMouseLeave={e=>e.currentTarget.style.transform="none"}>
                {p.popular && (
                  <div style={{ position:"absolute",top:-13,left:"50%",transform:"translateX(-50%)",background:p.color,color:C.bg,fontFamily:"'Share Tech Mono',monospace",fontSize:10,fontWeight:700,letterSpacing:2,padding:"4px 16px",borderRadius:20,whiteSpace:"nowrap" }}>
                    MOST POPULAR
                  </div>
                )}
                <div style={{ marginBottom:8 }}>
                  <span style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:p.color,letterSpacing:2,textTransform:"uppercase" }}>{p.name}</span>
                </div>
                <div className="heading" style={{ fontSize:52,fontWeight:900,color:p.popular?p.color:C.text,lineHeight:1,marginBottom:4 }}>
                  R{price.toLocaleString()}
                </div>
                <div style={{ fontSize:12,color:C.dim,marginBottom:28 }}>/ month{annual?" · billed annually":""}</div>
                <div style={{ borderTop:`1px solid ${C.border}`,paddingTop:24,marginBottom:24 }}>
                  {p.features.map((f,j)=>(
                    <div key={j} style={{ display:"flex",alignItems:"center",gap:10,marginBottom:10,fontSize:13,color:p.popular?C.text:C.dim }}>
                      <span style={{ color:p.color,fontSize:12 }}>✓</span>{f}
                    </div>
                  ))}
                </div>
                <Btn variant={p.popular?"primary":"outline"} full onClick={()=>onNav("register")} style={p.popular?{}:{ borderColor:p.color,color:p.color }}>
                  Start Free Trial
                </Btn>
              </div>
            );
          })}
        </div>

        {/* PayFast badge */}
        <div style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"24px 32px",display:"flex",alignItems:"center",gap:24,flexWrap:"wrap" }}>
          <div style={{ fontSize:32,color:C.accent }}>◈</div>
          <div style={{ flex:1 }}>
            <div className="heading" style={{ fontSize:20,fontWeight:700,letterSpacing:1,marginBottom:4 }}>PAYMENTS BY PAYFAST</div>
            <div style={{ fontSize:13,color:C.dim }}>South Africa's leading payment gateway · PCI DSS Level 1 compliant · All major SA banks supported · Instant EFT · 3.5% per transaction, no monthly gateway fee</div>
          </div>
          <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
            {["Visa","Mastercard","Instant EFT","Mobicred"].map(m=>(
              <span key={m} style={{ background:C.surface,border:`1px solid ${C.border}`,borderRadius:4,padding:"5px 12px",fontSize:11,fontFamily:"'Share Tech Mono',monospace",color:C.dim }}>{m}</span>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
}

// ── Testimonials ───────────────────────────────────────────
function Testimonials() {
  const ref = useRef(null);
  const vis = useInView(ref);

  const reviews = [
    { name:"James van der Berg", role:"CTO · FinTech SA",   text:"We run weekly scans across 30 servers. Sentinel found a Log4Shell instance our previous tool completely missed. The PDF reports are clean enough to send straight to the board.", stars:5 },
    { name:"Nandi Dlamini",       role:"IT Manager · Retail Group", text:"Set up in under an hour. The scheduled scans mean I don't have to remember to run tests — I just wake up to a report every Monday morning.", stars:5 },
    { name:"Pieter Louw",         role:"Founder · MSP",     text:"I resell Sentinel under my own brand. My clients think it's my software. The white-label reports look completely professional.", stars:5 },
  ];

  return (
    <Section style={{ background:C.surface,borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}` }}>
      <Container>
        <div ref={ref} style={{ textAlign:"center",marginBottom:56 }}>
          <Chip color={C.gold} style={{ marginBottom:16 }}>Testimonials</Chip>
          <h2 className="heading" style={{ fontSize:"clamp(32px,4vw,54px)",fontWeight:900,letterSpacing:2,marginTop:16 }}>
            TRUSTED BY SA <span style={{ color:C.gold }}>SECURITY TEAMS</span>
          </h2>
        </div>
        <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:20 }}>
          {reviews.map((r,i)=>(
            <div key={i} style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:28,animation:vis?`fadeUp .5s ${i*.1}s both`:"none" }}>
              <div style={{ display:"flex",gap:3,marginBottom:16 }}>
                {[...Array(r.stars)].map((_,j)=><span key={j} style={{ color:C.gold,fontSize:16 }}>★</span>)}
              </div>
              <p style={{ fontSize:14,color:C.dim,lineHeight:1.7,marginBottom:20,fontStyle:"italic" }}>"{r.text}"</p>
              <div>
                <div style={{ fontWeight:700,fontSize:14 }}>{r.name}</div>
                <div style={{ fontSize:12,color:C.dim }}>{r.role}</div>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
}

// ── FAQ ────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  const faqs = [
    ["Is it legal to use Sentinel?","Yes — when used on systems you own or have written authorisation to test. Unauthorised scanning is illegal. All targets require an authorisation confirmation before scanning."],
    ["What payment methods are accepted?","PayFast supports all major SA banks via Instant EFT, Visa, Mastercard, and Mobicred. Everything billed in ZAR with no foreign exchange fees."],
    ["Can I cancel anytime?","Yes. Cancel from your dashboard at any time. Your access continues until the end of your current billing period with no penalties."],
    ["Do I need technical skills to use Sentinel?","No. The wizard walks you through setup. You enter an IP address, choose a scan type, and we handle everything else including the report."],
    ["What is a 'target'?","A target is any system you want to scan — a server, website, network device, or IP range. Your plan determines how many targets you can manage simultaneously."],
    ["Can I white-label reports for my clients?","Yes on the Enterprise plan. Upload your logo, set your brand colours and company name — reports carry your branding with no Sentinel watermark."],
    ["Is my data secure?","Yes. All scan results are stored encrypted. We never share your data. You can delete your account and all associated data at any time."],
    ["Do you offer a free trial?","Yes — 14 days free on any plan. No credit card required to start. You only enter payment details when you're ready to continue."],
  ];

  return (
    <Section id="faq">
      <Container style={{ maxWidth:760 }}>
        <div style={{ textAlign:"center",marginBottom:56 }}>
          <Chip color={C.blue} style={{ marginBottom:16 }}>FAQ</Chip>
          <h2 className="heading" style={{ fontSize:"clamp(32px,4vw,54px)",fontWeight:900,letterSpacing:2,marginTop:16 }}>
            COMMON <span style={{ color:C.blue }}>QUESTIONS</span>
          </h2>
        </div>
        <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
          {faqs.map(([q,a],i)=>(
            <div key={i} style={{ background:C.card,border:`1px solid ${open===i?C.accent+"50":C.border}`,borderRadius:10,overflow:"hidden",transition:"border-color .2s" }}>
              <button onClick={()=>setOpen(open===i?null:i)} style={{ width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 22px",background:"none",border:"none",cursor:"pointer",textAlign:"left" }}>
                <span style={{ fontWeight:600,fontSize:15,color:C.text }}>{q}</span>
                <span style={{ color:open===i?C.accent:C.dim,fontSize:20,transition:"transform .2s",transform:open===i?"rotate(45deg)":"none",flexShrink:0,marginLeft:12 }}>+</span>
              </button>
              {open===i && (
                <div style={{ padding:"0 22px 18px",fontSize:14,color:C.dim,lineHeight:1.7,animation:"slideDown .2s ease" }}>{a}</div>
              )}
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
}

// ── CTA ────────────────────────────────────────────────────
function CTA({ onNav }) {
  return (
    <Section style={{ background:`linear-gradient(135deg,${C.surface} 0%,${C.card} 100%)`,borderTop:`1px solid ${C.border}` }}>
      <GridBg opacity={0.1} />
      <div style={{ position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:600,height:400,background:`radial-gradient(ellipse,${C.accentLo} 0%,transparent 70%)`,pointerEvents:"none" }} />
      <Container style={{ textAlign:"center",position:"relative" }}>
        <div style={{ fontSize:48,marginBottom:16 }}>⬡</div>
        <h2 className="heading" style={{ fontSize:"clamp(36px,6vw,72px)",fontWeight:900,letterSpacing:2,marginBottom:20 }}>
          READY TO FIND YOUR<br /><span style={{ color:C.accent }}>VULNERABILITIES?</span>
        </h2>
        <p style={{ fontSize:17,color:C.dim,marginBottom:36,maxWidth:460,margin:"0 auto 36px" }}>
          Join 600+ South African businesses already using Sentinel to secure their systems. Start your free trial today.
        </p>
        <div style={{ display:"flex",gap:14,justifyContent:"center",flexWrap:"wrap",marginBottom:20 }}>
          <Btn variant="primary" size="lg" onClick={()=>onNav("register")}>Start Free 14-Day Trial →</Btn>
          <Btn variant="outline" size="lg" href="#pricing">View Pricing</Btn>
        </div>
        <div style={{ fontSize:13,color:C.dim }}>No credit card required · Cancel anytime · PayFast secure billing</div>
      </Container>
    </Section>
  );
}

// ── Legal modal ────────────────────────────────────────────
const LEGAL_CONTENT = {
  privacy: {
    title: "Privacy Policy",
    updated: "1 May 2026",
    body: `
**1. Information We Collect**
We collect information you provide directly: name, email address, company name, phone number, and billing details. We also collect technical data from scans you initiate — including IP addresses, hostnames, open ports and vulnerability data — which belong to you and systems you are authorised to test.

**2. How We Use Your Information**
- To provide and operate the Sentinel platform
- To send scan reports, invoices and account notifications via email
- To process payments through PayFast (we do not store card details)
- To improve platform reliability and security

**3. Data Storage**
All data is stored on Supabase (PostgreSQL) hosted in the EU (AWS eu-west-1). Scan results are encrypted at rest. We do not sell or share your data with third parties except as required to operate the service (PayFast, Resend email).

**4. Data Retention**
Scan results and reports are retained for 24 months or until you delete your account. You may request deletion of all your data at any time by contacting support@sentinel.co.za.

**5. Cookies**
We use a single session token stored in localStorage. No third-party tracking cookies.

**6. Your Rights (POPIA)**
Under the Protection of Personal Information Act (POPIA) you have the right to access, correct or delete personal information we hold about you. Contact our Information Officer at support@sentinel.co.za.

**7. Contact**
Information Officer: support@sentinel.co.za
`
  },
  terms: {
    title: "Terms of Service",
    updated: "1 May 2026",
    body: `
**1. Authorised Use Only**
Sentinel is a professional penetration testing tool. You may ONLY scan systems you own or have explicit written authorisation to test. Scanning without authorisation is illegal under the Electronic Communications and Transactions Act (ECT Act) and the Cybercrimes Act.

**2. Subscription and Billing**
Subscriptions are billed monthly in ZAR via PayFast. Prices are as listed on the pricing page. You may cancel anytime; access continues until the end of the current billing period. No refunds for partial months.

**3. Free Trial**
A 14-day free trial is provided with no credit card required. Trial accounts are limited to 3 targets.

**4. Prohibited Uses**
You may not use Sentinel to scan systems without authorisation, launch denial of service attacks, extract data from systems you do not control, or violate any applicable law.

**5. Limitation of Liability**
Sentinel is provided as a tool for authorised security testing. We are not liable for any damages arising from misuse of the platform. You are solely responsible for ensuring you have authorisation before scanning any system.

**6. Termination**
We reserve the right to suspend or terminate accounts used in violation of these terms without notice or refund.

**7. Governing Law**
These terms are governed by the laws of the Republic of South Africa.

**8. Contact**
support@sentinel.co.za
`
  },
  authorized: {
    title: "Authorized Use Policy",
    updated: "1 May 2026",
    body: `
**Sentinel is for Authorised Penetration Testing Only**

Every scan initiated through Sentinel requires you to confirm who authorised the scan and on what date. This is a legal and ethical requirement.

**What is authorised scanning?**
Scanning a system you own, or scanning a system where you have received explicit written permission from the system owner to perform security testing.

**What is NOT authorised?**
- Scanning IP addresses or domains you do not own without written permission
- "Curiosity" scans of unknown hosts
- Scanning competitor infrastructure
- Automated mass scanning

**Your Responsibility**
By using Sentinel you confirm that:
1. You have authorisation for every target you scan
2. You will use findings only to improve security of the tested system
3. You will not use Sentinel to attack, disrupt, or gain unauthorised access to any system

**Reporting Misuse**
If you suspect Sentinel is being misused, report it immediately to: support@sentinel.co.za

**Legal Consequences**
Unauthorised computer access is a criminal offence under the Cybercrimes Act (Act 19 of 2020). Penalties include fines and imprisonment.
`
  },
  popia: {
    title: "POPIA Compliance Statement",
    updated: "1 May 2026",
    body: `
**Protection of Personal Information Act (POPIA) — Act 4 of 2013**

Sentinel PenTest Suite is committed to compliance with POPIA.

**Information Officer**
Our designated Information Officer is responsible for ensuring compliance. Contact: support@sentinel.co.za

**Personal Information We Process**
- Name, email address, company name, phone number
- Billing information (processed by PayFast — we do not store card details)
- Scan targets and vulnerability data you generate

**Legal Basis for Processing**
We process personal information to fulfil our contract with you (service delivery), to comply with legal obligations, and with your consent where required.

**Data Subject Rights**
Under POPIA you have the right to:
- Request access to your personal information
- Request correction of inaccurate information
- Request deletion of your information
- Object to processing in certain circumstances
- Lodge a complaint with the Information Regulator

**Information Regulator Contact**
The Information Regulator (South Africa)
JD House, 27 Stiemens Street, Braamfontein, Johannesburg
inforeg@justice.gov.za

**Transborder Transfers**
Data is stored on Supabase (AWS eu-west-1, Ireland). Email is processed via Resend (USA). Both providers maintain appropriate data protection standards.

**Retention**
Personal data is retained for 24 months after account closure, after which it is permanently deleted, unless a longer retention period is required by law.

**Contact Our Information Officer**
support@sentinel.co.za
`
  }
};

function LegalModal({ type, onClose }) {
  const content = LEGAL_CONTENT[type];
  if (!content) return null;

  const renderBody = (text) => text.trim().split("\n").map((line, i) => {
    if (line.startsWith("**") && line.endsWith("**")) {
      return <div key={i} style={{ fontWeight:700,color:C.text,marginTop:18,marginBottom:6,fontSize:14 }}>{line.slice(2,-2)}</div>;
    }
    if (line.startsWith("- ")) {
      return <div key={i} style={{ paddingLeft:16,marginBottom:4,fontSize:13,color:C.dim }}>• {line.slice(2)}</div>;
    }
    if (line.trim() === "") return <div key={i} style={{ height:6 }} />;
    return <div key={i} style={{ fontSize:13,color:C.dim,lineHeight:1.7,marginBottom:4 }}>{line}</div>;
  });

  return (
    <div style={{ position:"fixed",inset:0,background:"#00000090",zIndex:9000,display:"flex",alignItems:"center",justifyContent:"center",padding:20 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:C.card,border:`1px solid ${C.borderHi}`,borderRadius:12,width:"100%",maxWidth:680,maxHeight:"85vh",display:"flex",flexDirection:"column",animation:"fadeUp .25s ease" }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"20px 28px",borderBottom:`1px solid ${C.border}` }}>
          <div>
            <div style={{ fontWeight:800,fontSize:18,color:C.text }}>{content.title}</div>
            <div style={{ fontSize:11,color:C.dim,marginTop:3,fontFamily:"'Share Tech Mono',monospace" }}>Last updated: {content.updated}</div>
          </div>
          <button onClick={onClose} style={{ background:"none",border:"none",color:C.dim,cursor:"pointer",fontSize:22,lineHeight:1 }}>×</button>
        </div>
        <div style={{ overflowY:"auto",padding:"20px 28px",flex:1 }}>
          {renderBody(content.body)}
        </div>
        <div style={{ padding:"16px 28px",borderTop:`1px solid ${C.border}`,display:"flex",justifyContent:"flex-end" }}>
          <Btn variant="outline" size="sm" onClick={onClose}>Close</Btn>
        </div>
      </div>
    </div>
  );
}

// ── Footer ─────────────────────────────────────────────────
function Footer({ onNav, onLegal }) {
  const legalLinks = [
    ["Privacy Policy",    "privacy"],
    ["Terms of Service",  "terms"],
    ["Authorized Use",    "authorized"],
    ["POPIA",             "popia"],
  ];

  return (
    <footer style={{ background:C.surface,borderTop:`1px solid ${C.border}`,padding:"48px 0 24px" }}>
      <Container>
        <div style={{ display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:40,marginBottom:48 }}>
          <div>
            <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:16 }}>
              <span style={{ fontSize:20,color:C.accent }}>⬡</span>
              <span className="heading" style={{ fontSize:18,letterSpacing:2,fontWeight:800,color:C.accent }}>SENTINEL</span>
            </div>
            <p style={{ fontSize:13,color:C.dim,lineHeight:1.7,maxWidth:260,marginBottom:16 }}>
              Professional penetration testing SaaS for South African businesses. Authorised scanning only.
            </p>
            <div style={{ fontSize:12,color:C.muted }}>© {new Date().getFullYear()} Sentinel PenTest Suite</div>
          </div>
          {[
            { title:"Product",  links:[["Features","#features"],["Pricing","#pricing"],["Reports","#reports"],["API Docs","#"]] },
            { title:"Company",  links:[["About","#"],["Blog","#"],["Careers","#"],["Contact","#"]] },
          ].map(col=>(
            <div key={col.title}>
              <div style={{ fontWeight:700,fontSize:13,marginBottom:16,fontFamily:"'Share Tech Mono',monospace",letterSpacing:1,color:C.text }}>{col.title}</div>
              {col.links.map(([label,href])=>(
                <a key={label} href={href} style={{ display:"block",fontSize:13,color:C.dim,marginBottom:10,textDecoration:"none",transition:"color .15s" }} onMouseEnter={e=>e.target.style.color=C.text} onMouseLeave={e=>e.target.style.color=C.dim}>{label}</a>
              ))}
            </div>
          ))}
          <div>
            <div style={{ fontWeight:700,fontSize:13,marginBottom:16,fontFamily:"'Share Tech Mono',monospace",letterSpacing:1,color:C.text }}>Legal</div>
            {legalLinks.map(([label,key])=>(
              <button key={key} onClick={()=>onLegal(key)} style={{ display:"block",fontSize:13,color:C.dim,marginBottom:10,background:"none",border:"none",cursor:"pointer",padding:0,fontFamily:"'Barlow',sans-serif",transition:"color .15s",textAlign:"left" }}
                onMouseEnter={e=>e.target.style.color=C.text} onMouseLeave={e=>e.target.style.color=C.dim}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ borderTop:`1px solid ${C.border}`,paddingTop:24,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12 }}>
          <div style={{ fontSize:11,color:C.muted,fontFamily:"'Share Tech Mono',monospace",letterSpacing:1 }}>AUTHORISED USE ONLY · ILLEGAL TO SCAN WITHOUT PERMISSION</div>
          <div style={{ display:"flex",gap:16 }}>
            {["support@sentinel.co.za","0800 SENTINEL"].map(t=>(
              <span key={t} style={{ fontSize:12,color:C.dim,fontFamily:"'Share Tech Mono',monospace" }}>{t}</span>
            ))}
          </div>
        </div>
      </Container>
    </footer>
  );
}

// ── Root ───────────────────────────────────────────────────
export default function LandingPage({ onNav }) {
  const [legalOpen, setLegalOpen] = useState(null);

  return (
    <>
      <style>{CSS}</style>
      {legalOpen && <LegalModal type={legalOpen} onClose={()=>setLegalOpen(null)} />}
      <Nav onNav={onNav} />
      <Hero onNav={onNav} />
      <Ticker />
      <Stats />
      <Features />
      <ReportPreview />
      <Pricing onNav={onNav} />
      <Testimonials />
      <FAQ />
      <CTA onNav={onNav} />
      <Footer onNav={onNav} onLegal={setLegalOpen} />
    </>
  );
}
